import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Fingerprint, ShieldAlert, Search, Loader2, Pause, Play, AlertTriangle,
  CheckCircle2, XCircle, History, Mail, Phone, User, Briefcase, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchIdentities, fetchIdentityConflicts, fetchIdentityHistory,
  suspendIdentity, reactivateIdentity, quarantineIdentity, resolveIdentity,
} from "@admin/services/adminApi";
import { IdentitiesGuide } from "@admin/components/identities/IdentitiesGuide";

const STATUS_PILL = {
  active: { cls: "bg-emerald-100 text-emerald-800", label: "Active" },
  pending: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
  rejected: { cls: "bg-slate-100 text-slate-500", label: "Rejected" },
  suspended: { cls: "bg-rose-100 text-rose-800", label: "Suspended" },
  quarantined: { cls: "bg-orange-100 text-orange-800", label: "Quarantined" },
};

const ROLE_ICON = { user: User, lawyer: Briefcase, admin: ShieldAlert };

const TABS = [
  { key: "conflicts", label: "Conflicts", icon: AlertTriangle },
  { key: "all", label: "All identities", icon: Fingerprint },
  { key: "suspended", label: "Suspended", icon: Pause },
];

function StatusPill({ status, testid }) {
  const p = STATUS_PILL[status] || { cls: "bg-slate-100 text-slate-700", label: status };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.cls}`}
    >
      {p.label}
    </span>
  );
}

function RoleBadge({ role }) {
  const Icon = ROLE_ICON[role] || User;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0A2540] capitalize">
      <Icon size={12} /> {role}
    </span>
  );
}

function ReasonPrompt({ open, title, description, cta, onConfirm, onClose, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) setReason(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="identity-reason-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-5 shadow-xl">
        <h3 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{title}</h3>
        {description && <p className="mt-1 text-xs text-slate-500">{description}</p>}
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required for audit log)"
          rows={3}
          data-testid="identity-reason-input"
          className="mt-3 rounded-xl"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="identity-reason-cancel">Cancel</Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !reason.trim()}
            data-testid="identity-reason-confirm"
            className="adv-btn-primary rounded-xl"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            {cta}
          </Button>
        </div>
      </div>
    </div>
  );
}

function HistoryDrawer({ open, identity, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !identity) return;
    setBusy(true);
    fetchIdentityHistory(identity.id, 200)
      .then((d) => setRows(d.items || []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, [open, identity]);

  if (!open || !identity) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" data-testid="identity-history-drawer">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">History for</p>
            <p className="mt-1 text-sm font-bold text-[#0A2540]">
              {identity.email || identity.phone} · <RoleBadge role={identity.role} />
            </p>
          </div>
          <button onClick={onClose} data-testid="identity-history-close" className="text-slate-500 hover:text-[#0A2540]">
            <XCircle size={20} />
          </button>
        </div>

        <div className="mt-5 space-y-2">
          {busy && <p className="text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</p>}
          {!busy && rows.length === 0 && <p className="text-xs text-slate-400">No history entries.</p>}
          {rows.map((h) => (
            <div key={h.id} className="border border-slate-100 rounded-xl p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-[#0A2540]">{h.event}</span>
                <span className="text-slate-400">{h.created_at?.slice(0, 19).replace("T", " ")}</span>
              </div>
              {h.reason && <p className="mt-1 text-xs text-slate-600">{h.reason}</p>}
              {(h.before || h.after) && (
                <pre className="mt-2 text-[10px] text-slate-500 bg-slate-50 rounded-lg p-2 overflow-x-auto">
                  {JSON.stringify({ before: h.before, after: h.after }, null, 2)}
                </pre>
              )}
              {h.actor_id && <p className="mt-1 text-[10px] text-slate-400">actor: {h.actor_role || "?"} · {h.actor_id}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConflictCard({ item, onQuarantine, onResolve, onHistory }) {
  return (
    <div className="border border-orange-200 bg-orange-50 rounded-2xl p-4" data-testid={`identity-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-[#0A2540]">
            {item.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {item.email}</span>}
            {item.email && item.phone && <span className="mx-2 text-slate-300">·</span>}
            {item.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {item.phone}</span>}
          </p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <StatusPill status={item.status} testid={`identity-status-${item.id}`} />
            <RoleBadge role={item.role} />
            <span className="text-[10px] text-slate-500">entity: {item.entity_id?.slice(0, 12)}</span>
          </div>
          {item.quarantine_reason && (
            <p className="mt-2 text-xs text-orange-800">
              <AlertTriangle size={12} className="inline mr-1" />
              {item.quarantine_reason}
            </p>
          )}
          {item.conflicting_principals?.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Competing claims</p>
              {item.conflicting_principals.map((c) => (
                <div key={`${c.role}-${c.entity_id}`} className="text-xs bg-white border border-orange-200 rounded-lg px-2 py-1 inline-flex items-center gap-2 mr-2">
                  <RoleBadge role={c.role} /> <span className="text-slate-500">{c.entity_id}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onHistory(item)} data-testid={`identity-history-${item.id}`}>
            <History size={14} className="mr-1" /> History
          </Button>
          <Button size="sm" variant="outline" onClick={() => onResolve(item, "release")} data-testid={`identity-release-${item.id}`}>
            <XCircle size={14} className="mr-1" /> Reject claim
          </Button>
          <Button size="sm" onClick={() => onResolve(item, "activate")} className="adv-btn-primary" data-testid={`identity-activate-${item.id}`}>
            <CheckCircle2 size={14} className="mr-1" /> Approve claim
          </Button>
        </div>
      </div>
    </div>
  );
}

function IdentityRow({ item, onSuspend, onReactivate, onQuarantine, onHistory }) {
  return (
    <div className="border border-slate-200 rounded-2xl p-4" data-testid={`identity-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#0A2540] truncate">
            {item.email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {item.email}</span>}
            {item.email && item.phone && <span className="mx-2 text-slate-300">·</span>}
            {item.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {item.phone}</span>}
          </p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <StatusPill status={item.status} testid={`identity-status-${item.id}`} />
            <RoleBadge role={item.role} />
            <span className="text-[10px] text-slate-400">entity: {item.entity_id?.slice(0, 12)}</span>
            <span className="text-[10px] text-slate-400">updated: {item.updated_at?.slice(0, 10)}</span>
          </div>
          {item.quarantine_reason && (
            <p className="mt-2 text-xs text-rose-600">{item.quarantine_reason}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onHistory(item)} data-testid={`identity-history-${item.id}`}>
            <History size={14} className="mr-1" /> History
          </Button>
          {item.status === "suspended" ? (
            <Button size="sm" onClick={() => onReactivate(item)} className="adv-btn-primary" data-testid={`identity-reactivate-${item.id}`}>
              <Play size={14} className="mr-1" /> Reactivate
            </Button>
          ) : item.status === "active" ? (
            <>
              <Button size="sm" variant="outline" onClick={() => onQuarantine(item)} data-testid={`identity-quarantine-${item.id}`}>
                <AlertTriangle size={14} className="mr-1" /> Quarantine
              </Button>
              <Button size="sm" variant="outline" onClick={() => onSuspend(item)} data-testid={`identity-suspend-${item.id}`}>
                <Pause size={14} className="mr-1" /> Suspend
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminIdentities() {
  const [tab, setTab] = useState("conflicts");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [items, setItems] = useState([]);
  const [conflicts, setConflicts] = useState({ quarantined: [], active_duplicates: [] });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(null); // { title, action, item, cta }
  const [historyItem, setHistoryItem] = useState(null);

  const loadConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchIdentityConflicts();
      setConflicts(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load conflicts"); }
    finally { setLoading(false); }
  }, []);

  const loadList = useCallback(async (statusFilter) => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (role && role !== "all") params.role = role;
      if (q.trim()) params.q = q.trim();
      const d = await fetchIdentities(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load identities"); }
    finally { setLoading(false); }
  }, [q, role]);

  useEffect(() => {
    if (tab === "conflicts") loadConflicts();
    else if (tab === "suspended") loadList("suspended");
    else loadList(null);
  }, [tab, loadConflicts, loadList]);

  const refresh = () => {
    if (tab === "conflicts") loadConflicts();
    else if (tab === "suspended") loadList("suspended");
    else loadList(null);
  };

  const openPrompt = (title, description, cta, item, action) => {
    setPrompt({ title, description, cta, item, action });
  };

  const doAction = async (reason) => {
    if (!prompt) return;
    setBusy(true);
    try {
      const { item, action } = prompt;
      if (action === "suspend") await suspendIdentity(item.id, reason);
      else if (action === "reactivate") await reactivateIdentity(item.id, reason);
      else if (action === "quarantine") await quarantineIdentity(item.id, reason);
      else if (action === "resolve_activate") await resolveIdentity(item.id, "activate", reason);
      else if (action === "resolve_release") await resolveIdentity(item.id, "release", reason);
      toast.success("Done");
      setPrompt(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const quarantinedCount = conflicts.quarantined?.length || 0;
  const dupCount = conflicts.active_duplicates?.length || 0;

  return (
    <>
      <div className="mb-6" data-testid="admin-identities-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          <Fingerprint size={20} className="inline mr-2 text-[#D4AF37]" />
          Identity Registry
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Single source of truth for who owns each email and phone. Resolve conflicts, suspend abusive accounts, review the full audit trail.
        </p>
      </div>

      <IdentitiesGuide />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const badge =
            t.key === "conflicts" ? (quarantinedCount + dupCount) : null;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`identity-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                tab === t.key
                  ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]"
                  : "border-slate-200 text-slate-600 hover:border-[#0A2540]"
              }`}
            >
              <Icon size={12} /> {t.label}
              {badge != null && badge > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab !== "conflicts" && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex-1 min-w-[220px]">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, phone or entity id"
              data-testid="identity-search-input"
              className="rounded-xl"
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="identity-role-filter"
            className="rounded-xl border border-slate-200 h-10 px-3 text-sm"
          >
            <option value="all">All roles</option>
            <option value="user">User</option>
            <option value="lawyer">Lawyer</option>
            <option value="admin">Admin</option>
          </select>
          <Button onClick={refresh} disabled={loading} data-testid="identity-refresh" className="rounded-xl">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Search size={14} className="mr-1" />}
            Search
          </Button>
        </div>
      )}

      {tab === "conflicts" ? (
        <div className="space-y-3">
          {loading && <p className="text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading conflicts…</p>}
          {!loading && quarantinedCount === 0 && dupCount === 0 && (
            <div className="text-center bg-emerald-50 border border-emerald-200 rounded-2xl p-10" data-testid="identity-no-conflicts">
              <CheckCircle2 size={28} className="mx-auto text-emerald-600 mb-2" />
              <p className="text-sm font-bold text-emerald-800">All clear — no identity conflicts.</p>
              <p className="text-xs text-emerald-700 mt-1">The registry is fully consistent right now.</p>
            </div>
          )}
          {conflicts.quarantined?.map((item) => (
            <ConflictCard
              key={item.id}
              item={item}
              onHistory={(x) => setHistoryItem(x)}
              onResolve={(x, action) =>
                openPrompt(
                  action === "activate" ? "Approve this claim" : "Reject this claim",
                  action === "activate"
                    ? "This account keeps the email/phone: the row becomes ACTIVE and every competing claim on the same email or phone is rejected."
                    : "This account loses the email/phone: the row is marked REJECTED and can no longer sign in with it. Competing claims stay untouched.",
                  action === "activate" ? "Approve claim" : "Reject claim",
                  x,
                  action === "activate" ? "resolve_activate" : "resolve_release",
                )
              }
              onQuarantine={() => {}}
            />
          ))}
          {conflicts.active_duplicates?.map((grp) => (
            <div key={grp.email || grp.phone} className="border border-rose-200 bg-rose-50 rounded-2xl p-4">
              <p className="text-sm font-bold text-rose-800">
                Duplicate ACTIVE rows found for {grp.email || grp.phone} ({grp.count})
              </p>
              <p className="text-xs text-rose-600 mt-1">This should never happen — an operator should quarantine one of the rows.</p>
              <div className="mt-3 space-y-2">
                {grp.docs.map((d) => (
                  <IdentityRow
                    key={d.id}
                    item={d}
                    onHistory={(x) => setHistoryItem(x)}
                    onSuspend={(x) => openPrompt("Suspend identity", "Blocks logins immediately.", "Suspend", x, "suspend")}
                    onReactivate={(x) => openPrompt("Reactivate identity", null, "Reactivate", x, "reactivate")}
                    onQuarantine={(x) => openPrompt("Quarantine identity", "Freezes auth until an admin resolves.", "Quarantine", x, "quarantine")}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold mb-2">
            Showing {items.length} of {total}
          </p>
          <div className="space-y-2">
            {loading && <p className="text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="text-xs text-slate-400" data-testid="identity-empty">No identities match the current filter.</p>
            )}
            {items.map((item) => (
              <IdentityRow
                key={item.id}
                item={item}
                onHistory={(x) => setHistoryItem(x)}
                onSuspend={(x) => openPrompt("Suspend identity", `Blocks logins for ${x.email || x.phone}.`, "Suspend", x, "suspend")}
                onReactivate={(x) => openPrompt("Reactivate identity", null, "Reactivate", x, "reactivate")}
                onQuarantine={(x) => openPrompt("Quarantine identity", "Freezes auth until an admin resolves.", "Quarantine", x, "quarantine")}
              />
            ))}
          </div>
        </>
      )}

      <ReasonPrompt
        open={!!prompt}
        title={prompt?.title}
        description={prompt?.description}
        cta={prompt?.cta}
        onConfirm={doAction}
        onClose={() => setPrompt(null)}
        busy={busy}
      />
      <HistoryDrawer open={!!historyItem} identity={historyItem} onClose={() => setHistoryItem(null)} />
    </>
  );
}
